<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { useSchoolClassAccess } from '@/composables/schoolClassAccess/useSchoolClassAccess';
import { useSchoolClasses } from '@/composables/useSchoolClasses';
import { useSchoolLevelsBySchoolType } from '@/composables/useSchoolLevelsBySchoolType';
import CustomRadioButton from '@/core/elements/CustomRadioButton.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import ClassOrGroupBadge from '@/core/elements/ClassOrGroupBadge.vue';
import SchoolYearFilter from '@/core/SimpleDataTable/filters/SchoolYearFilter.vue';
import { computed, ref, toRefs, watch } from 'vue';

type TOption = {
  label: string;
  description: string;
  schoolClass: Components.Schemas.SchoolClassDto;
};

const { getResponsibleUserNames } = useSchoolClassAccess();
// bumping a group starts from the group itself, so this picker only ever lists classes
const { classes } = useSchoolClasses();
const { hasNextSchoolLevel } = useSchoolLevelsBySchoolType();

const props = defineProps<{
  selectedSchoolClass: Components.Schemas.SchoolClassDto | null;
}>();
const { selectedSchoolClass } = toRefs(props);

const emit = defineEmits<{
  continue: [schoolClass: Components.Schemas.SchoolClassDto | null];
  'update:isValid': [value: boolean];
}>();

const selectedSchoolClassInternal = ref<Components.Schemas.SchoolClassDto | null>(null);
const selectedSchoolYearId = ref<number | null>(null);

const upgradableClasses = computed(() => {
  return classes.value.filter((sC) => {
    const hasNoNext = !sC.nextClassId;
    return hasNoNext && hasNextSchoolLevel(sC.schoolLevel);
  });
});

const options = computed<TOption[]>(() => {
  return upgradableClasses.value
    .filter(
      (schoolClass) => selectedSchoolYearId.value == null || schoolClass.schoolYearId === selectedSchoolYearId.value,
    )
    .map((schoolClass) => ({
      label: schoolClass.name,
      description: getResponsibleUserNames(schoolClass),
      schoolClass,
    }));
});

const handleOptionSelected = (schoolClass: Components.Schemas.SchoolClassDto): void => {
  selectedSchoolClassInternal.value = schoolClass;
};

const handleSchoolYearSelected = (): void => {
  if (!selectedSchoolClassInternal.value) return;
  const isOptionVisible = options.value.some(
    (option) => option.schoolClass.id === selectedSchoolClassInternal.value?.id,
  );
  if (!isOptionVisible) {
    selectedSchoolClassInternal.value = null;
  }
};

const onSubmit = (): void => {
  emit('continue', selectedSchoolClassInternal.value);
};

const isSelected = (schoolClass: Components.Schemas.SchoolClassDto): boolean => {
  return selectedSchoolClassInternal.value?.id === schoolClass.id;
};

watch(
  selectedSchoolClassInternal,
  () => {
    emit('update:isValid', !!selectedSchoolClassInternal.value);
  },
  { immediate: true },
);

watch(
  selectedSchoolClass,
  () => {
    selectedSchoolClassInternal.value = selectedSchoolClass.value;
  },
  { immediate: true },
);
defineExpose({
  onSubmit,
});
</script>

<template>
  <form class="grid h-full w-full grid-rows-[min-content_min-content_1fr] px-lg md:px-content" @submit="onSubmit">
    <p class="text-subtitle mb-md">
      {{ $t('classes.bumpUpClassSelectClassDescription') }}
    </p>
    <SchoolYearFilter
      v-model="selectedSchoolYearId"
      class="!w-[220px]"
      theme="input"
      @update:model-value="handleSchoolYearSelected"
    />
    <div v-if="options.length" class="w-full flex flex-col gap-sm py-md">
      <div
        v-for="option in options"
        :key="option.label"
        :class="[
          'min-h-21 p-md rounded-md outline-inner',
          'flex items-center gap-sm',
          'transition duration-300 cursor-pointer',
          'hover:bg-surface-tertiary active:bg-surface-tertiary',
          isSelected(option.schoolClass) ? 'bg-primary/10' : 'bg-surface-secondary',
        ]"
        :aria-checked="isSelected(option.schoolClass)"
        tabindex="0"
        role="radio"
        @click="handleOptionSelected(option.schoolClass)"
        @keypress.space.stop="handleOptionSelected(option.schoolClass)"
        @keypress.enter.stop="handleOptionSelected(option.schoolClass)"
      >
        <CustomRadioButton
          name="bump-up-class-select-class"
          :model-value="isSelected(option.schoolClass)"
          @update:model-value="handleOptionSelected(option.schoolClass)"
        />
        <ClassOrGroupBadge :school-class="option.schoolClass" class="!size-[44px] mr-sm" />
        <div class="flex flex-col">
          <span class="font-semibold">{{ option.label }}</span>
          <span class="text-muted">
            {{ option.description }}
          </span>
        </div>
      </div>
    </div>
    <EmptyPlaceholder :data="options" class="py-md" />
  </form>
</template>
